"use client";

import { useEffect } from "react";

/**
 * Global client-error reporter + stale-chunk self-heal (2026-07-06).
 *
 * WHY: the studio "blink then crash" only happens in a user's REAL browser
 * session (stale cached JS chunks from an older build, or persisted local
 * state). A fresh incognito always loads the current build and works — which is
 * why the crash was never reproducible from our side. Telling users to open a
 * new browser is not a fix.
 *
 * This does two things:
 *  1. Reports every uncaught error / promise rejection to /api/client-error so
 *     the true stack lands in server logs (kubectl logs … | grep CV-CLIENT-ERROR).
 *  2. Self-heals the most common cause — a ChunkLoadError (the browser cached an
 *     old document that references chunk hashes the new build removed). Since our
 *     gated HTML is no-store, ONE hard reload pulls the fresh build. Guarded to
 *     fire at most once per session so it can never loop.
 */

const CHUNK_ERROR_RE =
	/ChunkLoadError|Loading chunk [\w-]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|'text\/html' is not a valid JavaScript MIME/i;

const RELOAD_FLAG = "cv-chunk-reloaded";

function report(kind: string, message: string, stack?: string, extra?: string) {
	try {
		const body = JSON.stringify({
			kind,
			message,
			stack,
			extra,
			url: typeof location !== "undefined" ? location.href : "",
			ua: typeof navigator !== "undefined" ? navigator.userAgent : "",
		});
		// keepalive so it still sends if the page is unloading/crashing.
		void fetch("/api/client-error", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body,
			keepalive: true,
		}).catch(() => {});
	} catch {
		/* ignore */
	}
}

function maybeSelfHeal(message: string): void {
	if (!CHUNK_ERROR_RE.test(message)) return;
	try {
		if (sessionStorage.getItem(RELOAD_FLAG)) return; // already tried this session
		sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
	} catch {
		/* private mode — reload once anyway is fine */
	}
	report("chunk-selfheal-reload", message);
	// Hard reload → fresh (no-store) HTML → current-build chunks.
	location.reload();
}

export function ClientErrorReporter() {
	useEffect(() => {
		const onError = (e: ErrorEvent) => {
			const msg = e.message || String(e.error?.message ?? e.error ?? "");
			report("window.onerror", msg, e.error?.stack);
			maybeSelfHeal(msg + " " + (e.error?.stack ?? ""));
		};
		const onRejection = (e: PromiseRejectionEvent) => {
			const reason = e.reason;
			const msg =
				typeof reason === "string" ? reason : String(reason?.message ?? reason ?? "");
			report("unhandledrejection", msg, reason?.stack);
			maybeSelfHeal(msg + " " + (reason?.stack ?? ""));
		};
		window.addEventListener("error", onError);
		window.addEventListener("unhandledrejection", onRejection);
		// Clear the once-per-session guard on a clean load so a genuinely new
		// stale-chunk situation later can still self-heal.
		try {
			const ts = Number(sessionStorage.getItem(RELOAD_FLAG) ?? 0);
			if (ts && Date.now() - ts > 60_000) sessionStorage.removeItem(RELOAD_FLAG);
		} catch {
			/* ignore */
		}
		return () => {
			window.removeEventListener("error", onError);
			window.removeEventListener("unhandledrejection", onRejection);
		};
	}, []);
	return null;
}

// Exposed so the StudioErrorBoundary can report render-phase crashes too.
export function reportClientError(kind: string, error: unknown, extra?: string) {
	const err = error as { message?: string; stack?: string };
	report(kind, String(err?.message ?? error ?? ""), err?.stack, extra);
	maybeSelfHeal(String(err?.message ?? "") + " " + (err?.stack ?? ""));
}
